import { apiBase } from './apiBase';
import { apiFetch, ApiError } from './api';
import { useAuthStore } from '../store/useAuthStore';

// ---------------------------------------------------------------------------
// Onboarding wizard API client (CLE-131).
//
// Mirrors the backend contract in backend/src/routes/onboarding.ts. The 4-step
// "import business data" wizard drives:
//   - GET  /api/onboarding/status                  — progress counts + booleans
//   - GET  /api/onboarding/template                — download the .xlsx template
//   - POST /api/onboarding/import/preview          — parse + validate (no DB)
//   - POST /api/onboarding/import/commit           — persist if clean
//   - POST /api/onboarding/bank-statement/preview  — parse + match a statement
//
// The three upload routes take the *raw file* as the request body (the file's
// own content type), not multipart — mirroring the storage proxy flow. The
// shared apiFetch() forces `Content-Type: application/json`, so uploads and the
// binary template download use a thin raw-fetch helper here instead.
// ---------------------------------------------------------------------------

interface Envelope<T> {
  success: true;
  data: T;
}

function authHeaders(): Record<string, string> {
  const token = useAuthStore.getState().accessToken;
  return token ? { Authorization: `Bearer ${token}` } : {};
}

// ---------------------------------------------------------------------------
// Status
// ---------------------------------------------------------------------------

export interface OnboardingCounts {
  clients: number;
  people: number;
  services: number;
  projects: number;
  allocations: number;
  smartbillInvoices: number;
  bankImports: number;
  suppliers: number;
  tasks: number;
  companyProfile: number;
}

export interface OnboardingSteps {
  companyProfile: boolean;
  company: boolean;
  excel: boolean;
  invoices: boolean;
  bank: boolean;
  suppliers: boolean;
  tasks: boolean;
}

export interface OnboardingStatus {
  counts: OnboardingCounts;
  steps: OnboardingSteps;
}

export async function getOnboardingStatus(): Promise<OnboardingStatus> {
  const res = await apiFetch<Envelope<OnboardingStatus>>('/api/onboarding/status', {
    headers: authHeaders(),
  });
  return res.data;
}

// ---------------------------------------------------------------------------
// Integrations / dependencies (CLE-130)
//
// Mirrors GET /api/onboarding/integrations — the dependency report that ties
// every external integration (SmartBill, ANAF, Google Calendar/Drive,
// Anthropic, OpenAI, SMTP) to its env vars + setup links. Never carries secret
// values; only whether each env var is set.
// ---------------------------------------------------------------------------

export type IntegrationState = 'connected' | 'configured' | 'missing' | 'optional';

export interface IntegrationEnvVar {
  name: string;
  set: boolean;
  required: boolean;
  secret: boolean;
  hint?: string;
}

export interface IntegrationStatus {
  key: string;
  label: string;
  description: string;
  required: boolean;
  state: IntegrationState;
  configured: boolean;
  envVars: IntegrationEnvVar[];
  missingRequired: string[];
  features: string[];
  setupUrl?: string;
  notes?: string;
}

export interface IntegrationSummary {
  total: number;
  configured: number;
  blocking: number;
}

export interface IntegrationsReport {
  integrations: IntegrationStatus[];
  summary: IntegrationSummary;
}

export async function getOnboardingIntegrations(probe = false): Promise<IntegrationsReport> {
  const res = await apiFetch<Envelope<IntegrationsReport>>(
    `/api/onboarding/integrations${probe ? '?probe=true' : ''}`,
    { headers: authHeaders() },
  );
  return res.data;
}

// ---------------------------------------------------------------------------
// Excel import
// ---------------------------------------------------------------------------

export interface ImportRowError {
  sheet: string;
  row: number; // 1-based
  column?: string;
  message: string;
}

export interface ParsedClient {
  name: string;
  cui?: string;
  contactPerson?: string;
  contactRole?: string;
  email?: string;
  phone?: string;
  address?: string;
}

export interface ParsedTeamMember {
  fullName: string;
  email: string;
  role: string;
  hourlyRate?: number;
  startDate?: string;
}

export interface ParsedService {
  name: string;
  description?: string;
  price?: number;
  unit?: string;
  category?: string;
}

export interface ParsedProject {
  name: string;
  code?: string;
  client: string;
  status?: string;
  startDate?: string;
  deadline?: string;
  budget?: number;
  contractType?: string;
  description?: string;
}

export interface ParsedAllocation {
  project: string;
  person: string;
  projectRole?: string;
  hoursPerWeek?: number;
}

export interface ImportPreview {
  clients: ParsedClient[];
  team: ParsedTeamMember[];
  services: ParsedService[];
  projects: ParsedProject[];
  allocations: ParsedAllocation[];
  errors: ImportRowError[];
  warnings: string[];
  valid: boolean;
}

export interface ImportCommitStat {
  created: number;
  updated: number;
}

export interface ImportCommitStats {
  clients: ImportCommitStat;
  team: ImportCommitStat;
  services: ImportCommitStat;
  projects: ImportCommitStat;
  allocations: ImportCommitStat;
}

export interface ImportCommitResult {
  stats: ImportCommitStats;
  warnings: string[];
}

/**
 * POST a raw file body to an onboarding upload route and return the JSON
 * envelope's `data`. Sends the file's own content type so Express's per-route
 * raw parser captures it (the route accepts any content type). On a non-2xx
 * response, throws an ApiError carrying the envelope's message + code; the
 * 422 import-validation case additionally exposes the row-level `details`.
 */
async function uploadRaw<T>(path: string, file: File): Promise<T> {
  const res = await fetch(`${apiBase()}${path}`, {
    method: 'POST',
    headers: {
      ...authHeaders(),
      'Content-Type': file.type || 'application/octet-stream',
    },
    body: file,
  });
  const text = await res.text();
  const parsed: unknown = text ? JSON.parse(text) : undefined;
  if (!res.ok) {
    let message = res.statusText || `Request failed: ${res.status}`;
    let code: string | undefined;
    let details: ImportRowError[] | undefined;
    if (parsed && typeof parsed === 'object' && 'error' in parsed) {
      const err = (parsed as { error: unknown }).error;
      if (err && typeof err === 'object') {
        const e = err as { message?: string; code?: string; details?: ImportRowError[] };
        message = e.message ?? message;
        code = e.code;
        details = e.details;
      }
    }
    throw new ImportApiError(res.status, message, code, details);
  }
  return (parsed as Envelope<T>).data;
}

/**
 * ApiError variant that also carries the row-level validation `details` the
 * commit route returns on a 422. The wizard re-renders the validation table
 * from these when a commit is rejected.
 */
export class ImportApiError extends ApiError {
  constructor(
    status: number,
    message: string,
    code: string | undefined,
    public details?: ImportRowError[],
  ) {
    super(status, message, code);
    this.name = 'ImportApiError';
  }
}

export function previewImport(file: File): Promise<ImportPreview> {
  return uploadRaw<ImportPreview>('/api/onboarding/import/preview', file);
}

export function commitImport(file: File): Promise<ImportCommitResult> {
  return uploadRaw<ImportCommitResult>('/api/onboarding/import/commit', file);
}

/**
 * Download the .xlsx import template and trigger a browser save. The route
 * streams a binary attachment, so this bypasses apiFetch's JSON handling.
 */
export async function downloadTemplate(): Promise<void> {
  const res = await fetch(`${apiBase()}/api/onboarding/template`, {
    headers: authHeaders(),
  });
  if (!res.ok) {
    throw new ApiError(res.status, 'Could not download the template.');
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'onboarding-clients-template.xlsx';
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// ---------------------------------------------------------------------------
// Bank statement
// ---------------------------------------------------------------------------

export type MatchConfidence = 'HIGH' | 'MEDIUM' | 'NONE';

export interface BankLine {
  id: string;
  date: string; // ISO yyyy-mm-dd
  description: string;
  amount: number; // always positive
  type: 'credit' | 'debit';
}

export interface OpenInvoice {
  id: string;
  number: string;
  partnerName?: string | null;
  partnerCif?: string | null;
  total: number;
  issueDate?: string | null;
  direction: 'credit' | 'debit';
}

export interface MatchResult {
  line: BankLine;
  invoice: OpenInvoice | null;
  confidence: MatchConfidence;
  score: number; // 0-100
  reason: string;
  suggestions: OpenInvoice[];
}

export interface BankPreview {
  bankCode: string;
  format: string;
  warnings: string[];
  totals: {
    total: number;
    matched: number;
    needsReview: number;
    unmatched: number;
  };
  matched: MatchResult[];
  needsReview: MatchResult[];
  unmatched: MatchResult[];
  /** SHA-256 hex of the uploaded file; carried into commit for dedup. */
  contentHash: string;
}

export function previewBankStatement(file: File): Promise<BankPreview> {
  const path = `/api/onboarding/bank-statement/preview?fileName=${encodeURIComponent(file.name)}`;
  return uploadRaw<BankPreview>(path, file);
}

/** One reviewed statement line the user confirms or ignores at commit time. */
export interface BankCommitLine {
  id: string;
  date: string;
  description: string;
  amount: number;
  type: 'credit' | 'debit';
  balance?: number | null;
  /** 'confirm' settles `invoiceId`; 'ignore' stores the line unreconciled. */
  action: 'confirm' | 'ignore';
  invoiceId?: string | null;
  confidence?: number;
}

export interface BankCommitInput {
  bankCode: string;
  format: string;
  fileName?: string | null;
  /** SHA-256 hex from the preview response (server-side dedup). */
  contentHash?: string | null;
  lines: BankCommitLine[];
}

export interface BankCommitResult {
  importId: string;
  totalRows: number;
  confirmedRows: number;
  ignoredRows: number;
  invoicesMarkedPaid: number;
}

/**
 * Persist the reviewed statement. Unlike the upload routes this is a normal
 * JSON POST (the decisions, not a file), so it goes through the shared apiFetch.
 */
export async function commitBankMatches(input: BankCommitInput): Promise<BankCommitResult> {
  const res = await apiFetch<Envelope<BankCommitResult>>('/api/onboarding/bank-statement/commit', {
    method: 'POST',
    headers: authHeaders(),
    body: JSON.stringify(input),
  });
  return res.data;
}
